import * as React from "react";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product as LegacyProduct } from "../../stores/product/Product";
import { Product as ApiProduct, usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation } from "randmar-api-client";
import ButtonWithDialogConfirm from "../util/ButtonWithDialogConfirm";
import { useToast } from "randmarcomps";

// Type guard to check if the product is a legacy MobX store class instance
const isLegacyProduct = (product: LegacyProduct | ApiProduct): product is LegacyProduct => product instanceof LegacyProduct;

interface Request3DScanProps {
  product: LegacyProduct | ApiProduct;
  children: React.ReactNode;
}

const Request3DScan: React.FC<Request3DScanProps> = ({ product, children }) => {
  const { applicationId } = useManufacturer();
  const { toast } = useToast();

  const [updateProduct] = usePostV4ManufacturerByRouteManufacturerIdProductAndProductNumberFieldNameMutation();

  const randmarSku = isLegacyProduct(product) ? product.randmarSKU : product.RandmarSKU;

  const handleConfirmRequest = async () => {
    if (!randmarSku) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Product SKU is missing.",
      });
      return;
    }

    try {
      await updateProduct({
        routeManufacturerId: applicationId,
        productNumber: randmarSku,
        fieldName: "Require3DScan",
        newValue: "true",
      }).unwrap();

      toast({
        variant: "success",
        title: "Success",
        description: "Request for 3D scan has been submitted.",
      });

      // Note: The original component re-fetched the product after this action.
      // With RTK Query, data fetching is handled automatically through cache invalidation tags,
      // which should be configured in the API slice.
      // Thus, a manual refetch is not necessary here.

    } catch {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to request 3D scan. Please try again.",
      });
    }
  };

  return (
    <ButtonWithDialogConfirm
      fab={!children}
      onConfirmation={handleConfirmRequest}
      buttonTitle="Ask the product to be 3D Scanned"
    >
      {children}
    </ButtonWithDialogConfirm>
  );
};

export default Request3DScan;
